import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { automunityAPI } from '../lib/api';
import { useAuthContext } from '../hooks/AuthContext';
import toast, { Toaster } from 'react-hot-toast';
import './Settings.css';

const ACCESS_ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full access to all resources' },
  { value: 'developer', label: 'Developer', description: 'View all, edit/delete own items' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only, can create requests' }
];

const Users: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthContext();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editingRoleUserId, setEditingRoleUserId] = useState<number | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await automunityAPI.getUsers({ include_inactive: true });
      const data = await response.body.json();
      const records = (data as any)?.records || (Array.isArray(data) ? data : []);
      setUsers(records);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data: any = {
      full_name: (form.elements.namedItem('full_name') as HTMLInputElement).value,
      email: (form.elements.namedItem('email') as HTMLInputElement).value,
      is_active: 1
    };
    if (isAdmin) {
      data.access_role = (form.elements.namedItem('access_role') as HTMLSelectElement)?.value || 'developer';
    }
    try {
      await automunityAPI.createUser(data);
      toast.success('User created successfully');
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      toast.error('Failed to create user');
    }
  };

  const handleRoleChange = async (userId: number, newRole: string) => {
    try {
      await automunityAPI.updateUserAccessRole(String(userId), newRole);
      setUsers(users.map(u => u.user_id === userId ? { ...u, access_role: newRole } : u));
      setEditingRoleUserId(null);
      toast.success('Access role updated');
    } catch (err) {
      console.error('Error updating role:', err);
      toast.error('Failed to update role');
    }
  };

  if (loading) return <div className="loading">Loading users...</div>;

  return (
    <>
      <Toaster position="top-right" />
      <div className="settings-page">
        <h1>Users</h1>
        <div className="portlet">
          <div className="portlet-body">
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={() => setShowInactive(!showInactive)}
                  className={`btn ${showInactive ? 'btn-secondary' : 'btn-secondary'}`}
                  style={{ whiteSpace: 'nowrap', opacity: showInactive ? 1 : 0.7 }}
                >
                  {showInactive ? 'Hide Inactive' : 'Show Inactive'}
                </button>
                {isAdmin && (
                  <button onClick={() => setShowModal(true)} className="btn btn-primary">Add User</button>
                )}
              </div>
              <input
                type="text"
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="form-control"
                style={{ maxWidth: '300px' }}
              />
            </div>
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Access Role</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users
                    .filter((user) => {
                      if (!showInactive && !user.is_active) return false;
                      if (!search) return true;
                      const q = search.toLowerCase();
                      return (
                        (user.full_name && user.full_name.toLowerCase().includes(q)) ||
                        (user.email && user.email.toLowerCase().includes(q))
                      );
                    })
                    .map((user) => (
                    <tr key={user.user_id} style={{ cursor: 'pointer' }}>
                      <td onClick={() => navigate(`/users/${user.user_id}`)}>{user.user_id}</td>
                      <td onClick={() => navigate(`/users/${user.user_id}`)}>{user.full_name}</td>
                      <td onClick={() => navigate(`/users/${user.user_id}`)}>{user.email}</td>
                      <td>
                        {isAdmin && editingRoleUserId === user.user_id ? (
                          <select
                            defaultValue={user.access_role || 'developer'}
                            onChange={(e) => handleRoleChange(user.user_id, e.target.value)}
                            onBlur={() => setEditingRoleUserId(null)}
                            autoFocus
                            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #4361ee' }}
                          >
                            {ACCESS_ROLES.map(r => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        ) : (
                          <span
                            onClick={(e) => { if (isAdmin) { e.stopPropagation(); setEditingRoleUserId(user.user_id); } }}
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              cursor: isAdmin ? 'pointer' : 'default',
                              background: user.access_role === 'admin' ? '#e8dbff' :
                                         user.access_role === 'developer' ? '#dbedff' : '#f0f0f0',
                              color: user.access_role === 'admin' ? '#6b21a8' :
                                     user.access_role === 'developer' ? '#1d4ed8' : '#555',
                              textTransform: 'capitalize' as const
                            }}
                            title={isAdmin ? 'Click to change role' : ''}
                          >
                            {user.access_role || 'developer'}
                          </span>
                        )}
                      </td>
                      <td onClick={() => navigate(`/users/${user.user_id}`)}>{user.is_active ? 'Yes' : 'No'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>No users available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Add User</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Full Name</label>
                  <input name="full_name" className="form-control" required />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input name="email" type="email" className="form-control" required />
                </div>
                {isAdmin && (
                  <div className="form-group">
                    <label>Access Role</label>
                    <select name="access_role" className="form-control" defaultValue="developer">
                      {ACCESS_ROLES.map(r => (
                        <option key={r.value} value={r.value}>{r.label} — {r.description}</option>
                      ))}
                    </select>
                  </div>
                )}
                <button type="submit" className="btn btn-success">Add</button>
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Users;
